/**
 * 일주(日柱) 계산.
 *
 * 정오(UT) 율리우스일 기반으로 산출한다.
 *
 * 기준점: 2019-01-27 = 甲子 (육십갑자 인덱스 0)
 * 2019-01-27 정오 JD = 2458511.0
 *
 * stemIndex = (JDnoon - 1) % 10, branchIndex = (JDnoon + 1) % 12
 * sexagenaryIndex = (JDnoon + 49) % 60
 * 검증: JD 2458511 → (2458511 + 49) % 60 = 0 (甲子)
 *
 * 야자시(23:00-23:59)는 다음날 일주를 사용한다.
 * 자시(23:00-01:00)가 사주상 다음날에 속하기 때문이다.
 */

import type { Pillar } from '../types/pillars';
import { makePillar } from '../core/sexagenary';
import { jdAtNoon } from '../astronomy/julian-day';
import { addDays } from '../calendar/date-utils';

/**
 * 일주를 계산한다.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @param hour - 시 (0-23)
 * @returns 일주
 */
export function getDayPillar(
  year: number,
  month: number,
  day: number,
  hour: number,
): Pillar {
  let adjustedYear = year;
  let adjustedMonth = month;
  let adjustedDay = day;

  // 야자시 (23:00-23:59): use next day's pillar
  if (hour >= 23) {
    const nextDay = addDays(year, month, day, 1);
    adjustedYear = nextDay.year;
    adjustedMonth = nextDay.month;
    adjustedDay = nextDay.day;
  }

  return getDayPillarRaw(adjustedYear, adjustedMonth, adjustedDay);
}

/**
 * 야자시 보정 없이 일주를 반환한다.
 * 호출자가 직접 날짜 보정을 처리하는 경우에 사용한다.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @returns 일주
 */
export function getDayPillarRaw(
  year: number,
  month: number,
  day: number,
): Pillar {
  // jdAtNoon returns an integer JD value (noon UT)
  const jdNoon = jdAtNoon(year, month, day);
  const jdInt = Math.floor(jdNoon);

  // Calculate sexagenary index using the reference offset
  const sexagenaryIndex = ((jdInt + 49) % 60 + 60) % 60;
  return makePillar(sexagenaryIndex);
}
